import { CheckSquare, RefreshCw, Square } from "lucide-react";
import {
  ADMIN_RBAC_CATALOG,
  ACTIONS as ALL_ACTIONS,
  SCREEN_ACCESS_ACTION_ORDER,
} from "../../../rbac/catalog";

// ---------------------------------------------------------------------------
// Wizard Step 3 - Screen Access Configuration
//
// Stored shape (per role):
//   {
//     <moduleKey>: {
//       view|create|edit|delete|approve|export: boolean,   // module-level (OR of screens + legacy flags)
//       screens: {
//         <screenKey>: { view, create, edit, delete, approve, export }
//       }
//     }
//   }
//
// Only actions declared in the catalog for each screen are shown (flat row).
// Granting any mutating permission implies View for that screen. Unchecking View
// clears mutators (handled in normalizeScreenPermissionRow + useAccessCrud).
// ---------------------------------------------------------------------------

/** Actions for this screen in catalog order (must match toggle-all sequence). */
function actionsForScreenInOrder(screen) {
  const allowed = new Set(screen.actions || []);
  return SCREEN_ACCESS_ACTION_ORDER.filter((a) => allowed.has(a));
}

/**
 * One horizontal row of permission checkboxes — same `action` keys and handler
 * as the previous grouped UI; order follows SCREEN_ACCESS_ACTION_ORDER.
 */
function ScreenAccessActionCheckboxes({
  screen,
  screenPerms,
  roleId,
  selectedModuleKey,
  onScreenPermissionChange,
  readOnly,
}) {
  const ordered = actionsForScreenInOrder(screen);
  if (ordered.length === 0) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {ordered.map((action) => {
        const name =
          screen.actionLabels?.[action] ||
          action.charAt(0).toUpperCase() + action.slice(1);
        const where = screen.actionWhere?.[action];
        const fullTitle = where ? `${name} ${where}` : name;
        return (
          <label
            key={action}
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow-sm transition-all ${
              readOnly ? "cursor-default opacity-70" : "hover:border-redhalo-red/40"
            }`}
            title={fullTitle}
          >
            <input
              type="checkbox"
              checked={!!screenPerms[action]}
              disabled={readOnly}
              onChange={(e) =>
                onScreenPermissionChange(
                  roleId,
                  selectedModuleKey,
                  screen.key,
                  action,
                  e.target.checked
                )
              }
              className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300 accent-redhalo-red focus:ring-redhalo-red"
              aria-label={fullTitle}
            />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">{name}</div>
              {where ? (
                <div className="mt-1 text-xs text-gray-500">{where}</div>
              ) : null}
            </div>
          </label>
        );
      })}
    </div>
  );
}

const emptyPermissions = () => {
  const out = {};
  for (const a of ALL_ACTIONS) out[a] = false;
  return out;
};

const isAllSelected = (perms, allowed) =>
  allowed.every((k) => !!perms?.[k]);
const isAnySelected = (perms, allowed) =>
  allowed.some((k) => !!perms?.[k]);

const getModuleAccess = (currentAccess, moduleKey) => {
  const mod = currentAccess[moduleKey];
  if (!mod || typeof mod !== "object") return { screens: {} };
  return { ...mod, screens: { ...(mod.screens || {}) } };
};

const moduleSummary = (currentAccess, moduleDef) => {
  const mod = getModuleAccess(currentAccess, moduleDef.key);
  const screens = moduleDef.screens;
  const allGranted = screens.every((s) =>
    isAllSelected(mod.screens[s.key], s.actions)
  );
  const someGranted = screens.some((s) =>
    isAnySelected(mod.screens[s.key], s.actions)
  );
  return { allGranted, someGranted };
};

export default function ScreenAccessView({
  selectedRoleForAccess,
  wizardRoleId,
  screenAccess,
  selectedModuleForAccess,
  setSelectedModuleForAccess,
  onScreenPermissionChange,
  onSelectAllModule,
  onSaveAndNext,
  onPrevious,
  onCancel,
  readOnly = false,
  catalog = ADMIN_RBAC_CATALOG,
}) {
  const roleId = selectedRoleForAccess || wizardRoleId;
  if (!roleId) {
    return (
      <div className="flex-1 p-4">
        <div className="flex h-full items-center justify-center rounded-xl border border-gray-200 bg-white p-6 text-center shadow-card">
          <p className="text-sm text-gray-500">
            Please select a role to configure screen access
          </p>
        </div>
      </div>
    );
  }

  const currentAccess = screenAccess[roleId] || {};
  const selectedModule =
    catalog.find((m) => m.key === selectedModuleForAccess) || null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#fafafa]" style={{ height: "100%" }}>
      <form className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 min-h-0 overflow-hidden p-4">
          <div className="flex h-full w-full overflow-hidden rounded-t-table-lg border border-gray-200 bg-white shadow-card">
            <ModuleSidebar
              roleId={roleId}
              catalog={catalog}
              currentAccess={currentAccess}
              selectedModuleForAccess={selectedModuleForAccess}
              setSelectedModuleForAccess={setSelectedModuleForAccess}
              onSelectAllModule={onSelectAllModule}
              readOnly={readOnly}
            />

            <div className="flex flex-1 flex-col overflow-hidden">
              {selectedModule ? (
                <ModuleScreensPanel
                  roleId={roleId}
                  selectedModule={selectedModule}
                  currentAccess={currentAccess}
                  onSelectAllModule={onSelectAllModule}
                  onScreenPermissionChange={onScreenPermissionChange}
                  readOnly={readOnly}
                />
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-gray-500">
                    Select a module from the sidebar to configure screen access
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-t border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100/90 px-5 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
        <button
          type="button"
          onClick={onPrevious}
          className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-redhalo-red/30"
        >
          Previous
        </button>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[44px] rounded-lg border-2 border-gray-400 bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:border-gray-500 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSaveAndNext}
            disabled={readOnly}
            className="min-h-[44px] rounded-lg bg-redhalo-red px-5 py-2 text-sm font-semibold text-white shadow-md transition-[opacity,box-shadow] hover:opacity-95 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-redhalo-red/50"
          >
            Next: Data access
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleSidebar({
  roleId,
  catalog,
  currentAccess,
  selectedModuleForAccess,
  setSelectedModuleForAccess,
  onSelectAllModule,
  readOnly,
}) {
  return (
    <div className="flex w-64 flex-shrink-0 flex-col border-r border-gray-200 bg-[#fafafa]">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
        <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">Modules</h4>
        <button
          type="button"
          onClick={() => setSelectedModuleForAccess(catalog[0]?.key || null)}
          className="rounded-md border border-gray-300 p-1 text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
          title="Reset selection"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e0 #f7fafc" }}
      >
        {catalog.map((mod) => {
          const { allGranted, someGranted } = moduleSummary(currentAccess, mod);
          const isSelected = selectedModuleForAccess === mod.key;
          return (
            <button
              key={mod.key}
              type="button"
              onClick={() => setSelectedModuleForAccess(mod.key)}
              className={`w-full border-b border-gray-200 px-3 py-2.5 text-left transition-colors ${
                isSelected
                  ? "border-l-4 border-l-redhalo-red bg-white"
                  : "border-l-4 border-l-transparent hover:bg-gray-100/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div
                    className={`font-semibold text-sm ${
                      isSelected ? "text-redhalo-navy" : "text-gray-900"
                    }`}
                  >
                    {mod.label}
                  </div>
                  <div className="text-xs text-gray-500">
                    {mod.screens.length} screen(s)
                  </div>
                </div>
                <div className="ml-2">
                  <input
                    type="checkbox"
                    checked={allGranted}
                    disabled={readOnly}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectAllModule(mod.key, roleId, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 cursor-pointer"
                    title={
                      allGranted || someGranted
                        ? "Deselect all permissions in this module"
                        : "Select all permissions in this module"
                    }
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuickPanelScreensPanel({
  roleId,
  selectedModule,
  currentAccess,
  onScreenPermissionChange,
  onSelectAllModule,
  readOnly,
}) {
  const mod = getModuleAccess(currentAccess, selectedModule.key);
  const { allGranted } = moduleSummary(currentAccess, selectedModule);

  return (
    <>
      <div
        className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-orange-300/60 px-4 py-3"
        style={{
          background: "linear-gradient(90deg, #f96332 0%, #f4b977 100%)",
        }}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h4 className="text-sm font-bold uppercase tracking-wide text-white">
            {selectedModule.label}
          </h4>
          <span className="rounded-md border border-white/40 bg-white/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
            {selectedModule.screens.length} item
            {selectedModule.screens.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center rounded-xl border border-white/35 bg-white/10 p-1 pl-2 shadow-sm backdrop-blur-[2px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectAllModule(selectedModule.key, roleId, !allGranted);
            }}
            disabled={readOnly}
            className="min-h-[40px] rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {allGranted ? "Deselect all" : "Select all"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-gradient-to-b from-[#fafafa] to-gray-100/80 p-4">
        <div
          className="grid h-full min-h-[220px] w-full grid-cols-2 grid-rows-6 items-stretch justify-items-stretch gap-x-4 gap-y-2.5"
          role="group"
          aria-label="Quick panel shortcuts"
        >
          {selectedModule.screens.map((screen) => {
            const screenPerms = mod.screens[screen.key] || emptyPermissions();
            const isEnabled = !!screenPerms.view;
            return (
              <label
                key={screen.key}
                className={[
                  "group flex min-h-[48px] min-w-0 items-center gap-3 rounded-xl border px-3 py-2.5 shadow-sm transition-colors",
                  readOnly
                    ? "cursor-default border-gray-200 bg-white opacity-70"
                    : "cursor-pointer",
                  !readOnly && isEnabled
                    ? "border-redhalo-red/35 bg-white ring-1 ring-redhalo-red/15 hover:border-redhalo-red/50 hover:bg-orange-50/50"
                    : !readOnly &&
                        "border-gray-200/90 bg-white hover:border-gray-300 hover:bg-gray-50/90",
                  !readOnly &&
                    "focus-within:border-redhalo-red/50 focus-within:ring-2 focus-within:ring-redhalo-red/25 focus-within:ring-offset-1",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <input
                  type="checkbox"
                  checked={isEnabled}
                  disabled={readOnly}
                  title={screen.label}
                  aria-label={screen.label}
                  onChange={(e) =>
                    onScreenPermissionChange(
                      roleId,
                      selectedModule.key,
                      screen.key,
                      "view",
                      e.target.checked
                    )
                  }
                  className="h-6 w-6 shrink-0 cursor-pointer rounded border-gray-300 text-redhalo-red accent-redhalo-red focus:ring-redhalo-red disabled:cursor-not-allowed"
                />
                <span
                  className="min-w-0 flex-1 select-none text-left text-sm font-medium leading-snug text-gray-800 line-clamp-2 group-hover:text-gray-900"
                  title={screen.label}
                >
                  {screen.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ModuleScreensPanel({
  roleId,
  selectedModule,
  currentAccess,
  onSelectAllModule,
  onScreenPermissionChange,
  readOnly,
}) {
  const mod = getModuleAccess(currentAccess, selectedModule.key);
  const { allGranted, someGranted } = moduleSummary(currentAccess, selectedModule);
  const isQuickPanel = selectedModule.key === "ess_quick_panel";

  if (isQuickPanel) {
    return (
      <QuickPanelScreensPanel
        roleId={roleId}
        selectedModule={selectedModule}
        currentAccess={currentAccess}
        onSelectAllModule={onSelectAllModule}
        onScreenPermissionChange={onScreenPermissionChange}
        readOnly={readOnly}
      />
    );
  }

  return (
    <>
      <div className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h4 className="text-sm font-bold uppercase tracking-wide text-gray-800">
            {selectedModule.label}
          </h4>
          <span className="rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
            {selectedModule.screens.length} screen
            {selectedModule.screens.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center rounded-xl border border-white/35 bg-white/10 p-1 pl-2 shadow-sm backdrop-blur-[2px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectAllModule(selectedModule.key, roleId, !allGranted);
            }}
            disabled={readOnly}
            className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {allGranted ? "Deselect all" : "Select all"}
          </button>
          {allGranted ? (
            <CheckSquare className="h-5 w-5 text-redhalo-red" />
          ) : someGranted ? (
            <Square className="h-5 w-5 text-amber-500" />
          ) : (
            <Square className="h-5 w-5 text-gray-300" />
          )}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto overflow-x-hidden bg-[#fafafa] p-4"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e0 #f7fafc" }}
      >
        <div className="grid grid-cols-1 gap-3">
          {selectedModule.screens.map((screen) => {
            const screenPerms = mod.screens[screen.key] || emptyPermissions();
            const allScreenPerms = isAllSelected(screenPerms, screen.actions);

            const toggleAll = (e) => {
              e.stopPropagation();
              const nextValue = !allScreenPerms;
              const ordered = SCREEN_ACCESS_ACTION_ORDER.filter((a) =>
                screen.actions.includes(a)
              );
              const sequence = nextValue ? ordered : [...ordered].reverse();
              sequence.forEach((action) =>
                onScreenPermissionChange(
                  roleId,
                  selectedModule.key,
                  screen.key,
                  action,
                  nextValue
                )
              );
            };

            return (
              <div
                key={screen.key}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <span className="text-sm font-semibold leading-snug text-gray-900">
                        {screen.label}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={toggleAll}
                      disabled={readOnly}
                      className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-800 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {allScreenPerms ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="flex flex-col gap-3">
                    <ScreenAccessActionCheckboxes
                      screen={screen}
                      screenPerms={screenPerms}
                      roleId={roleId}
                      selectedModuleKey={selectedModule.key}
                      onScreenPermissionChange={onScreenPermissionChange}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
